import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BulbOutlined } from '@ant-design/icons';
import { ROUTES, OTP_RESEND_TIME } from '@utils/constants';
import { validatePhoneNumber, getPhoneError, validateOTP } from '@utils/validators';
import { initiateSignIn, signUpUser, verifyOtp, resendOtp } from '@api/auth';
import { getStudentsByParentId } from '@api/user';
import { useAuth } from '../../context/AuthContext';
import { useToastContext } from '../../context/ToastContext';
import { ModalAlert } from '@components/ui/Alert/ModalAlert';
import { PhoneInput } from '@components/auth/PhoneInput';
import { OTPInput } from '@components/ui/OTPInput/OTPInput';
import { InfoCard } from '@components/ui/InfoCard';
import './styles/welcome.css';
import '@styles/loader.css';
import AppHeader from '../../components/layout/AppHeader';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, logout, isAuthenticated, isLoading: authLoading, setIsNewUser } = useAuth();
  const { addToast } = useToastContext();
  
  // Phone state
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  
  // OTP state
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [session, setSession] = useState('');
  
  // New user flag
  const [isNewUserSignup, setIsNewUserSignup] = useState(false);
  
  // Resend timer
  const [resendTimer, setResendTimer] = useState(OTP_RESEND_TIME);
  const [canResend, setCanResend] = useState(false);
  
  // Profile validation alert
  const [showProfileAlert, setShowProfileAlert] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(ROUTES.WELCOME, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Resend timer countdown
  useEffect(() => {
    if (showOTP && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (showOTP && resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer, showOTP]);

  const handleSendOTP = async () => {
    const error = getPhoneError(phone);
    if (error) {
      setPhoneError(error);
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');
    
    try {
      // Try to sign in first (existing user)
      const response = await initiateSignIn(phone);
      setSession(response.session);
      setShowOTP(true);
      setIsNewUserSignup(false);
      setResendTimer(OTP_RESEND_TIME);
      setCanResend(false);
    } catch (err: any) {
      // User not found - sign up without name
      if (err?.message === 'USER_NOT_FOUND') {
        try {
          const signupResponse = await signUpUser(phone);
          setSession(signupResponse.session);
          setShowOTP(true);
          setIsNewUserSignup(true);
          setIsNewUser(true);
          setResendTimer(OTP_RESEND_TIME);
          setCanResend(false);
        } catch (signupErr: any) {
          addToast({
            type: 'error',
            title: 'Account Creation Failed',
            message: signupErr?.message || 'Failed to create account. Please try again.',
          });
        }
      } else {
        addToast({
          type: 'error',
          title: 'OTP Send Failed',
          message: err?.message || 'Failed to send OTP. Please try again.',
        });
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyOTP = async () => {    
    if (!validateOTP(otp)) {
      console.warn('[Login] Invalid OTP format');
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    
    try {   
      const authResult = await verifyOtp(phone, otp, session);

      // Check if user profile is PARENT
      if (authResult.user.profile !== 'PARENT') {
        console.error('[Login] User profile is not PARENT:', authResult.user.profile);
        
        // Show alert modal first (this keeps the user on the page)
        setShowProfileAlert(true);
        setOtpLoading(false);
        
        return;
      }
      
      // Login with tokens
      login(authResult.accessToken, authResult.refreshToken, authResult.idToken);
      
      // Check if user has students (for existing users)
      if (!isNewUserSignup) {
        try {
          const parentId = authResult.user.preferred_username || authResult.user.username;
          const studentsResponse = await getStudentsByParentId(parentId);
          
          if (studentsResponse?.students && studentsResponse.students.length > 0) {
            // Existing user with students - go to dashboard
            navigate(ROUTES.WELCOME, { replace: true });
          } else {
            // Existing user but no students - go to student details
            navigate(ROUTES.STUDENT_DETAILS, { replace: true });
          }
        } catch (error) {
          console.error('[Login] Error checking students:', error);
          // Error checking students - go to dashboard
          navigate(ROUTES.WELCOME, { replace: true });
        }
      } else {
        // New user - always go to student details
        navigate(ROUTES.STUDENT_DETAILS, { replace: true });
      }
    } catch (err: any) {
      console.error('[Login] Error during OTP verification:', {
        error: err.message,
        stack: err.stack,
      });
      
      addToast({
        type: 'error',
        title: 'OTP Verification Failed',
        message: err?.message || 'Invalid OTP. Please try again.',
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpLoading(true);
    try {
      setOtp('');
      setOtpError('');
      const response = await resendOtp(phone);
      setSession(response.session);
      setResendTimer(OTP_RESEND_TIME);
      setCanResend(false);
      addToast({
        type: 'success',
        title: 'OTP Resent',
        message: 'A new OTP has been sent to your phone.',
      });
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Resend Failed',
        message: err?.message || 'Failed to resend OTP. Please try again.',
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setShowOTP(false);
    setSession('');
    setOtp('');
    setOtpError('');
    setIsNewUserSignup(false);
    setResendTimer(OTP_RESEND_TIME);
    setCanResend(false);
  };


  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="welcome-page">
        <div className="welcome-page__loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="welcome-page">
      {/* Modal Alert for Profile Validation */}
      <ModalAlert
        type="error"
        title="Cannot Proceed Right Now"
        description="We're unable to process your request at this moment. Please contact support for further assistance."
        visible={showProfileAlert}
        onConfirm={() => {
          setShowProfileAlert(false);
          logout();
        }}
        confirmText="Okay"
      />

      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <div className="welcome-page__container">
        <div className="welcome-page__content">
          {/* Badge */}
          <div className="welcome-page__badge">
            ⚡ DONE IN UNDER 60 SECONDS
          </div>

          {/* Title Section */}
          <div className="welcome-page__title-section">
            <h1 className="welcome-page__title">Safe & Reliable<br />Student Mobility</h1>
            <p className="welcome-page__subtitle">
              Fastest routes. Verified drivers.<br />
              Campus-aligned schedules.
            </p>
            <div className="welcome-page__accent-line"></div>
          </div>

          {/* Form Section */}
          <div className="welcome-page__form">
            {/* Phone Input - Always visible */}
            <div className="welcome-page__form-group">
              <PhoneInput
                value={phone}
                onChange={(value) => {
                  setPhone(value);
                  setPhoneError('');
                }}
                error={phoneError}
                disabled={showOTP || phoneLoading}
                onHelperTextClick={showOTP ? handleBackToPhone : undefined}
              />
            </div>

            {/* OTP Input - Show only after phone is submitted */}
            {showOTP && (
              <div className="welcome-page__form-group welcome-page__form-group--otp">
                <OTPInput
                  length={6}
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    setOtpError('');
                  }}
                  error={otpError}
                  disabled={otpLoading}
                />
                
                <div className="welcome-page__resend">
                  {!canResend ? (
                    <p className="welcome-page__resend-timer">
                      Resend in 0:{resendTimer.toString().padStart(2, '0')}
                    </p>
                  ) : (
                    <button 
                      className="welcome-page__resend-btn"
                      onClick={handleResendOTP}
                      disabled={otpLoading}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Button - Right after form inputs */}
            <button
              onClick={!showOTP ? handleSendOTP : handleVerifyOTP}
              disabled={
                !showOTP 
                  ? !validatePhoneNumber(phone) || phoneLoading
                  : otp.length !== 6 || otpLoading
              }
              className={`welcome-page__button ${phoneLoading || otpLoading ? 'btn-loading' : ''}`}
            >
              {phoneLoading ? (
                <span className="btn-loader btn-loader--dark"></span>
              ) : otpLoading ? (
                <>
                  <span className="btn-loader btn-loader--dark"></span>
                  Verifying...
                </>
              ) : (
                !showOTP ? 'Get Started' : 'Verify & Continue'
              )}
            </button>
          </div>

          {/* Info Card */}
          <InfoCard
            icon={<BulbOutlined style={{ fontSize: 28, color: '#FFD700' }} />}
            title="Did you know?"
            description="Omni's seating capacity is 5 including the driver. Anything more is non compliant & illegal."
          />

          {/* Trusted By Section */}
          <div className="welcome-page__trusted">
            <p className="welcome-page__trusted-label">TRUSTED BY PARENTS OF STUDENTS FROM</p>
            <div className="welcome-page__logos">
              <img src="/allen.png" alt="Allen" className="welcome-page__logo" />
              <img src="/pw.png" alt="PW" className="welcome-page__logo" />
              <img src="/spin.png" alt="Spin" className="welcome-page__logo" />
            </div>
          </div>

          {/* Terms */}
          <p className="welcome-page__terms">
            By continuing, you agree to our <a href="https://zeromoblt.com/terms" className="welcome-page__terms-link">Terms of Service</a>
          </p>

          {/* Loading Modal Overlay */}
          {(phoneLoading || otpLoading) && (
            <div className="welcome-page__modal-overlay">
              <div className="welcome-page__modal">
                <span className="btn-loader btn-loader--dark" style={{ width: 40, height: 40 }}></span>
                <h3>{phoneLoading ? 'Sending OTP' : 'Verifying OTP'}</h3>
                <p>Please wait while we {phoneLoading ? 'send the verification code' : 'verify your number'}...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
